import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import type { Project } from '@/stores/sequencerStore'

/**
 * React Query wrapper around the /api/v1/sequencer/projects
 * endpoints. The sync hook (`useSyncSequencerProjects`) drives
 * these — components generally shouldn't reach for the mutations
 * directly. The list query is still useful on its own for a
 * "my projects" picker.
 */

export interface ProjectListEntry {
  id: string
  name: string
  bpm: number
  kitId: string
  stepCount: number
  createdAt: string
  updatedAt: string
  /** 'owner' for the user's own projects, 'editor' / 'viewer'
   *  for projects shared with them. */
  role: 'owner' | 'editor' | 'viewer'
  /** Owner's user id — for owned projects equals the caller. */
  ownerId: string
  /** Owner's email — for shared projects lets the UI show
   *  "Shared by x@y.com". For owned projects equals the caller. */
  ownerEmail?: string
}

export interface ProjectFull extends Project {
  /** Optimistic-concurrency token; required on PUT. */
  version: number
  ownerId: string
  createdAt: string
  updatedAt: string
}

const LIST_KEY = ['sequencerProjects']
const CONTAINER_LIST_KEY = ['sequencerContainers']

/**
 * List the caller's Projects (containers). Light payload — just the
 * pointer rows for building a project picker without fetching every
 * container's full bank/pattern state.
 */
export interface ContainerListEntry {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  activeBankId: 'A' | 'B' | 'C' | 'D'
  bankCounts: Record<'A' | 'B' | 'C' | 'D', number>
  role: 'owner' | 'editor' | 'viewer'
  ownerId: string
  ownerEmail?: string
}

export function useSequencerContainers(enabled = true) {
  return useQuery<ContainerListEntry[]>({
    queryKey: CONTAINER_LIST_KEY,
    queryFn: async () =>
      apiClient.get<ContainerListEntry[]>('/api/v1/sequencer/containers'),
    enabled,
    staleTime: 30_000,
  })
}

/**
 * Legacy list query — still wired to the old pattern-row endpoint
 * for back-compat. Prefer `useSequencerContainers` for new UI.
 */
export function useSequencerProjects(enabled = true) {
  return useQuery<ProjectListEntry[]>({
    queryKey: LIST_KEY,
    queryFn: async () =>
      apiClient.get<ProjectListEntry[]>('/api/v1/sequencer/projects'),
    enabled,
    staleTime: 30_000,
  })
}

/** Lazy-fetch a single project's full state. */
export function useSequencerProject(id: string | null, enabled = true) {
  return useQuery<ProjectFull>({
    queryKey: ['sequencerProject', id],
    queryFn: async () =>
      apiClient.get<ProjectFull>(`/api/v1/sequencer/projects/${id}`),
    enabled: enabled && !!id,
    staleTime: 10_000,
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation<
    ProjectFull,
    Error,
    Partial<Project> & { name: string; stepCount: number }
  >({
    mutationFn: async input =>
      apiClient.post<ProjectFull>('/api/v1/sequencer/projects', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

export function useUpdateProject() {
  const qc = useQueryClient()
  return useMutation<
    ProjectFull,
    Error,
    { id: string; version: number; patch: Partial<Project> }
  >({
    mutationFn: async ({ id, version, patch }) =>
      apiClient.put<ProjectFull>(`/api/v1/sequencer/projects/${id}`, {
        ...patch,
        version,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
      qc.invalidateQueries({ queryKey: ['sequencerProject', vars.id] })
    },
  })
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async id =>
      apiClient.delete<{ success: boolean }>(
        `/api/v1/sequencer/projects/${id}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

// ── Container mutations (Phase 7) ─────────────────────────

const CONTAINER_KEY = ['sequencerContainers']

export function useCreateContainer() {
  const qc = useQueryClient()
  return useMutation<
    { id: string; name: string; version: number },
    Error,
    { name: string }
  >({
    mutationFn: async input =>
      apiClient.post<{ id: string; name: string; version: number }>(
        '/api/v1/sequencer/containers',
        {
          name: input.name,
          activeBankId: 'A',
          banks: {
            A: {
              id: 'A',
              label: 'Bank A',
              kitId: 'classic',
              midiPads: [],
              midiEnabled: true,
              activePatternId: '',
              patterns: {},
            },
            B: {
              id: 'B',
              label: 'Bank B',
              kitId: 'classic',
              midiPads: [],
              midiEnabled: true,
              activePatternId: '',
              patterns: {},
            },
            C: {
              id: 'C',
              label: 'Bank C',
              kitId: 'classic',
              midiPads: [],
              midiEnabled: true,
              activePatternId: '',
              patterns: {},
            },
            D: {
              id: 'D',
              label: 'Bank D',
              kitId: 'classic',
              midiPads: [],
              midiEnabled: true,
              activePatternId: '',
              patterns: {},
            },
          },
          songMode: false,
          song: [],
          slicerConfig: { sampleIds: [] },
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTAINER_KEY })
    },
  })
}

export function useDeleteContainer() {
  const qc = useQueryClient()
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: async id =>
      apiClient.delete<{ success: boolean }>(
        `/api/v1/sequencer/containers/${id}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTAINER_KEY })
    },
  })
}

// ── Jam — per-user scratchpad ─────────────────────────────

export interface JamBlob {
  ownerId: string
  name: string
  activeBankId: 'A' | 'B' | 'C' | 'D'
  banks: unknown
  songMode: boolean
  song: unknown[]
  midiConfig?: unknown
  slicerConfig: { sampleIds: string[]; activeSampleId?: string }
  bankCounts: Record<'A' | 'B' | 'C' | 'D', number>
  createdAt: string
  updatedAt: string
}

const JAM_KEY = ['sequencerJam']

export function useJam(enabled = true) {
  return useQuery<JamBlob | null>({
    queryKey: JAM_KEY,
    queryFn: async () => apiClient.get<JamBlob | null>('/api/v1/sequencer/jam'),
    enabled,
    staleTime: 30_000,
  })
}

export function usePutJam() {
  const qc = useQueryClient()
  return useMutation<JamBlob, Error, Partial<JamBlob> & { name: string }>({
    mutationFn: async input =>
      apiClient.put<JamBlob>('/api/v1/sequencer/jam', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JAM_KEY })
    },
  })
}

export function usePromoteJam() {
  const qc = useQueryClient()
  return useMutation<{ id: string }, Error, { name?: string }>({
    mutationFn: async input =>
      apiClient.post<{ id: string }>('/api/v1/sequencer/jam/promote', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: JAM_KEY })
      qc.invalidateQueries({ queryKey: CONTAINER_KEY })
    },
  })
}
